import { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { getMeetingProxyEligibility } from '../../services/general-meeting.service';
import { ProxyEligibilityDto } from '../../services/dtos/proxy-eligibility.dto';
import { useSnackbars } from '../../hooks/snackbar.hook';
import { showAxiosMessageError } from '../../utils/error-message.util';

interface MeetingProxyDialogProps {
    open: boolean;
    meetingId: string;
    loading?: boolean;
    onClose: () => void;
    onGiveToMember: (recipientReunionEluId: string) => void;
    onGiveToSuppleant: () => void;
}

const SUPPLEANT_VALUE = 'suppleant';

const suppleantReasonLabel = (reason?: string | null): string => {
    switch (reason) {
        case 'no_mandate':
            return 'Impossible de déterminer votre mandat pour cette réunion.';
        case 'no_active_suppleant':
            return "Aucun suppléant actif n'est associé à votre mandat.";
        case 'multiple_active_suppleants':
            return "Plusieurs suppléants actifs sont associés à votre mandat, veuillez contacter l'organisateur de la réunion.";
        default:
            return '';
    }
};

export default function MeetingProxyDialog(props: Readonly<MeetingProxyDialogProps>) {
    const [eligibility, setEligibility] = useState<ProxyEligibilityDto>();
    const [fetching, setFetching] = useState(false);
    const [selected, setSelected] = useState('');
    const showMessage = useSnackbars();

    useEffect(() => {
        if (!props.open) {
            setEligibility(undefined);
            setSelected('');
            return;
        }

        let isMounted = true;
        setFetching(true);
        getMeetingProxyEligibility(props.meetingId)
            .then(result => {
                if (isMounted) {
                    setEligibility(result);
                }
            })
            .catch(error => {
                showAxiosMessageError(error as Error, showMessage);
            })
            .finally(() => {
                if (isMounted) {
                    setFetching(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [props.open, props.meetingId, showMessage]);

    const handleConfirm = () => {
        if (!selected) {
            return;
        }

        if (selected === SUPPLEANT_VALUE) {
            props.onGiveToSuppleant();
        } else {
            props.onGiveToMember(selected);
        }
    };

    const suppleant = eligibility?.suppleant;
    const suppleantDisabled = !suppleant?.available || !suppleant?.candidate || Boolean(suppleant?.atCap);

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Donner mon pouvoir</DialogTitle>
            <DialogContent>
                {fetching && <Typography variant="body2">Chargement...</Typography>}
                {!fetching && eligibility && (
                    <FormControl component="fieldset" fullWidth>
                        <RadioGroup value={selected} onChange={event => setSelected(event.target.value)}>
                            {eligibility.eligibleMembers.map(member => (
                                <FormControlLabel
                                    key={member.id ?? member.eluId}
                                    value={member.id ?? ''}
                                    disabled={!member.id || member.atCap}
                                    control={<Radio />}
                                    label={
                                        member.atCap
                                            ? `${member.displayName} (nombre maximum de pouvoirs atteint)`
                                            : member.displayName
                                    }
                                />
                            ))}
                            <FormControlLabel
                                value={SUPPLEANT_VALUE}
                                disabled={suppleantDisabled}
                                control={<Radio />}
                                label={
                                    suppleant?.available && suppleant.candidate
                                        ? suppleant.atCap
                                            ? `Mon suppléant : ${suppleant.candidate.displayName} (nombre maximum de pouvoirs atteint)`
                                            : `Mon suppléant : ${suppleant.candidate.displayName}`
                                        : 'Mon suppléant'
                                }
                            />
                        </RadioGroup>
                        {!suppleant?.available && (
                            <Typography variant="body2" color="text.secondary">
                                {suppleantReasonLabel(suppleant?.reason)}
                            </Typography>
                        )}
                        {eligibility.eligibleMembers.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Aucun autre participant de la réunion ne peut recevoir votre pouvoir pour le moment.
                            </Typography>
                        )}
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} disabled={props.loading}>
                    Fermer
                </Button>
                <Button variant="contained" onClick={handleConfirm} disabled={props.loading || fetching || !selected}>
                    Donner mon pouvoir
                </Button>
            </DialogActions>
        </Dialog>
    );
}
